import { create } from 'zustand';
import { idpApi } from '../services/api/idpApi';

// Central initial state for easy reset
const initialState = {
  goals: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  pageInfo: {
    endCursor: null,
    hasNextPage: false,
    totalCount: 0
  },
  searchTerm: '',
  orderBy: 'createdAt',
  orderDirection: 'desc',
  limit: 30,
  statusFilter: null,
  // UI State
  scrollPosition: 0,
  lastCardId: null,
  isReturningFromDetails: false,
  uiSearchInput: '',
  selectedStatuses: [],
};

const useIDPStoreMyGoals = create((set, get) => {
  // Helper: param construction
  const buildParams = (overrides = {}) => {
    const state = get();
    return {
      limit: state.limit,
      cursor: overrides.cursor !== undefined ? overrides.cursor : state.pageInfo.endCursor,
      orderBy: state.orderBy,
      orderDirection: state.orderDirection,
      search: overrides.search !== undefined ? overrides.search : state.searchTerm,
      statusFilter: overrides.statusFilter !== undefined ? overrides.statusFilter : state.statusFilter,
      ...overrides,
    };
  };

  // Generic fetcher (reset/append)
  const fetchGoals = async ({ reset = false, overrides = {} } = {}) => {
    if (get().isLoading || get().isLoadingMore) return null;

    if (reset) set({ isLoading: true, error: null });
    else set({ isLoadingMore: true, error: null });

    const params = buildParams(reset ? { ...overrides, cursor: null } : overrides);

    try {
      const response = await idpApi.getMyDevelopmentGoals(params);

      if (response) {
        const newGoals = reset ? response.items : [...get().goals, ...response.items];
        const pageInfo = {
          ...response.pageInfo,
          hasNextPage: newGoals.length < response.pageInfo.totalCount,
        };
        set({
          goals: newGoals,
          pageInfo,
          isLoading: false,
          isLoadingMore: false,
          error: null,
        });
        return response;
      }
      set({ isLoading: false, isLoadingMore: false });
      return null;
    } catch (err) {
      set({
        error: err.message || 'Failed to load my development goals',
        isLoading: false,
        isLoadingMore: false,
      });
      return null;
    }
  };

  return {
    ...initialState,

    // UI State Actions
    setScrollPosition: (position) => set({ scrollPosition: position }),
    setLastCardId: (id) => set({ lastCardId: id }),
    setIsReturningFromDetails: (flag) => set({ isReturningFromDetails: flag }),
    setUISearchInput: (input) => set({ uiSearchInput: input }),
    setSelectedStatuses: (statuses) => {
      set({ selectedStatuses: statuses });
      const statusFilter = statuses.length > 0 ? statuses.map(s => s.id) : null;
      get().setStatusFilter(statusFilter);
    },

    // Filters
    setSearchTerm: (searchTerm, skipRefetch = false) => {
      set({ searchTerm });
      if (!skipRefetch) get().fetchGoalsWithReset({ search: searchTerm });
    },
    setOrderBy: (orderBy, skipRefetch = false) => {
      set({ orderBy });
      if (!skipRefetch) get().fetchGoalsWithReset({ orderBy });
    },
    setOrderDirection: (orderDirection, skipRefetch = false) => {
      set({ orderDirection });
      if (!skipRefetch) get().fetchGoalsWithReset({ orderDirection });
    },
    setLimit: (limit, skipRefetch = false) => {
      set({ limit });
      if (!skipRefetch) get().fetchGoalsWithReset({ limit });
    },
    setStatusFilter: (statusFilter, skipRefetch = false) => {
      set({ statusFilter });
      if (!skipRefetch) get().fetchGoalsWithReset({ statusFilter });
    },

    // Data actions
    fetchGoalsWithReset: (overrides = {}) => fetchGoals({ reset: true, overrides }),
    fetchGoalsLoadMore: () => fetchGoals({ reset: false }),

    updateGoal: (updatedGoal) => {
      if (!updatedGoal || !updatedGoal.id) {
        return false;
      }
      const { goals, pageInfo } = get();
      const index = goals.findIndex(goal => goal.id === updatedGoal.id);

      if (index !== -1) {
        // Update existing goal
        const updatedGoals = [...goals];
        updatedGoals[index] = { ...updatedGoals[index], ...updatedGoal };
        set({ goals: updatedGoals });
        return true;
      } else {
        // Add new goal and increment total count
        const updatedPageInfo = {
          ...pageInfo,
          totalCount: pageInfo.totalCount + 1
        };
        set({
          goals: [updatedGoal, ...goals],
          pageInfo: updatedPageInfo
        });
        return true;
      }
    },

    removeGoal: (goalId) => {
      if (!goalId) return false;

      const { goals, pageInfo } = get();
      const index = goals.findIndex(goal => goal.id === goalId);

      if (index !== -1) {
        const updatedGoals = goals.filter(goal => goal.id !== goalId);
        const updatedPageInfo = {
          ...pageInfo,
          totalCount: Math.max(0, pageInfo.totalCount - 1)
        };
        set({
          goals: updatedGoals,
          pageInfo: updatedPageInfo
        });
        return true;
      }
      return false;
    },

    // Filters reset
    resetFilters: () => {
      set({
        searchTerm: '',
        uiSearchInput: '',
        selectedStatuses: [],
        statusFilter: null,
      });
    },

    // State reset
    resetState: () => set({
      ...initialState,
    }),

    // Infinite loader helper
    shouldLoadMore: (lastVisibleIndex, threshold = 10) => {
      const { goals, pageInfo, isLoadingMore } = get();
      return !isLoadingMore &&
             pageInfo.hasNextPage &&
             lastVisibleIndex >= goals.length - threshold;
    },

    getGoalByIndex: (index) => {
      const { goals } = get();
      return goals[index] || null;
    },
  };
});

export default useIDPStoreMyGoals;
